import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IGetEmployees } from '../../../models/employees.model';
import {
  doGetStaff,
  doAddCampusToStaff,
  setStaffNone,
} from '../../../store/staff.slice';
import { doGetHousing } from '../../../store/housing.slice';
import { doRemoveAuth, doSwitchAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import qs from 'qs';
import _ from 'lodash';
import styled from 'styled-components';
import { useSnackbar } from 'notistack';
import Loader from '../../../components/Loader';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Footer from '../../../components/layout/Footer';
import Button from '../../../components/Button';
import {
  errorVariant,
  infoVariant,
  successVariant,
  warningVariant,
} from '../../../lib/barVariant';
import StaffTable from '../../../components/campus/staff/Table';
import StaffSearch from '../../../components/campus/staff/Search';
import StaffSummary from '../../../components/campus/staff/Summary';
import StaffInfo from '../../../components/campus/staff/Info';
import StaffResume from '../../../components/campus/staff/Resume';
import StaffContract from '../../../components/campus/staff/Contract';
import StaffHousing from '../../../components/campus/staff/Housing';
import StaffHousingEditor from '../../../components/campus/staff/HousingEditor';
import StaffCounsel from '../../../components/campus/staff/Counsel';
import AddCampusModal from '../../../components/modals/AddCampusModal';
import ExtendButton from '../../../components/ExtendButton';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import { getEmployees } from '../../../api/employees.api';
import ErrorResponse from '../../../components/ErrorResponse';
import useUtil from '../../../hooks/useUtil';
import Split from 'react-split';
import TableEmpty from '../../../components/common/TableEmpty';
import i18n from '../../../lib/i18n';

const Block = styled.div`
  .close-left {
    transform: translate(-33%);
  }

  .table-inside {
    width: 100%;

    th {
      background: ghostwhite;
    }
  }

  .table-basic th,
  td {
    vertical-align: middle;
  }
`;

export enum EmployeeContents {
  Info,
  Resume,
  Contract,
  Counsel,
  Housing,
  HousingEdit,
}

interface IHousingInput {
  [key: string]: string;
  contractSquare: string;
  contractDate: string;
  contractExpireDate: string;
  leaseholderName: string;
  leaseholderTelephone: string;
  bankCode: string;
  contractBrn: string;
  totalWrtAmt: string;
  monthLeaseAmount: string;
  zipNo: string;
  address: string;
  detailAddress: string;
  accountNo: string;
  brokerName: string;
  brokerTelephone: string;
  attachFileCode: string;
  originalName: string;
}

const housingInputInit = {
  contractSquare: '',
  contractDate: '',
  contractExpireDate: '',
  leaseholderName: '',
  leaseholderTelephone: '',
  bankCode: '',
  contractBrn: '',
  totalWrtAmt: '',
  monthLeaseAmount: '',
  zipNo: '',
  address: '',
  detailAddress: '',
  accountNo: '',
  brokerName: '',
  brokerTelephone: '',
  attachFileCode: '',
  originalName: '',
};

const CampusStaff: FC = () => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { isCampusDeactived } = useUtil();
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { staff, isLoading: staffIsLoading } = useSelector(
    (state: RootState) => state.staff,
  );
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  const { housing, isLoading: housingIsLoading } = useSelector(
    (state: RootState) => state.housing,
  );
  const { data: res, isLoading, error, execute } = useAxios<IGetEmployees>();

  const [isExtended, setIsExtended] = useState(false);
  const [memberCode, setMemberCode] = useState('');
  const [groupNo, setGroupNo] = useState('');
  const [keyword, setKeyword] = useState('');
  const [param, setParam] = useState({});
  const [content, setContent] = useState<EmployeeContents>(
    EmployeeContents.Info,
  );
  const [campusDeactived, setCampusDeactived] = useState(false);

  const [addCampusVisible, setAddCampusVisible] = useState(false);
  const [campusToAdd, setCampusToAdd] = useState<string | undefined>();
  const [housingInput, setHousingInput] =
    useState<IHousingInput>(housingInputInit);

  //0716 직원 재직 상태 state 추가
  const [employeeStaus, setEmployeeStatus] = useState('');

  useEffect(() => {
    setCampusDeactived(isCampusDeactived());
  }, [isCampusDeactived]);

  useEffect(() => {
    execute(getEmployees({ all: 'Y' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 쿼리 들어왔을 때 직원 데이터 fetch
  useEffect(() => {
    if (query.tab) {
      const tab = Number(query.tab);
      setContent(tab);
      setParam({ ...param, tab });
    }

    if (query.memberCode) {
      const _memberCode = String(query.memberCode);
      const tab = Number(query.tab);

      if (memberCode !== _memberCode) {
        setMemberCode(_memberCode);
        getStaff(_memberCode);
        getHousing(_memberCode);
        setHousingInput(housingInputInit);

        if (tab === EmployeeContents.HousingEdit) {
          setContent(EmployeeContents.Housing);
        }
      }
    } else {
      setMemberCode('');
      dispatch(setStaffNone());
    }
  }, [location]);

  useEffect(() => {
    history.push(`/campus/staff?${qs.stringify({ ...param, page: 1 })}`);
  }, [currentCampus]);

  const getStaff = async (id: string) => {
    const res = await dispatch(doGetStaff(id));

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(
          res?.data?.data || t('common.message.error'),
          errorVariant,
        );
    }
  };

  const getHousing = async (memberCode: string) => {
    const res = await dispatch(doGetHousing(memberCode));

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(
          res?.data?.data || t('common.message.error'),
          errorVariant,
        );
    }
  };

  const onSetTab = (_tab: number) => {
    const _param = { ...param, tab: _tab };
    history.push(`/campus/staff?${qs.stringify(_param)}`);
  };

  const onSearch = (
    memberCode: string | null = null,
    page: number | null = null,
  ) => {
    const param: any = { page: page || 1, tab: content };
    if (currentCampus) {
      param.campus = currentCampus;
    }
    if (groupNo) {
      param.groupNo = groupNo;
    }
    if (keyword) {
      param.keyword = keyword;
    }
    if (memberCode) {
      param.memberCode = memberCode;
    }
    if (employeeStaus) {
      param.employeeStaus = employeeStaus;
    }
    setParam(param);
    history.push(`/campus/staff?${qs.stringify(param)}`);
  };

  const addCampus = async () => {
    if (!staff) return;
    if (!campusToAdd) {
      return enqueueSnackbar(t('campus.staff.message1'), infoVariant);
    }

    const res = await dispatch(
      doAddCampusToStaff(staff.memberCode, {
        campusCode: [...staff.campusCode, campusToAdd],
      }),
    );

    switch (res?.status) {
      case 200:
        enqueueSnackbar(t('common.message.add'), successVariant);
        dispatch(doGetStaff(staff.memberCode));
        setAddCampusVisible(false);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(
          res?.data?.data || t('common.message.error'),
          errorVariant,
        );
    }
  };

  const onEditStaff = () => {
    if (!staff) {
      return enqueueSnackbar(t('campus.staff.message2'), infoVariant);
    }
    history.push(`/campus/staff/${staff?.memberCode}/edit${location.search}`);
  };

  const renderCampusName = (campusCode: string[]) => {
    if (!campusCode || !campusData) return;

    let name = '';
    campusCode.forEach((code: string) => {
      campusData.forEach((item) => {
        if (item.campusCode === code) {
          name += (i18n.language === 'ko' ? item.campusKorName : item.campusEngName) + '/';
        }
      });
    });

    return name.slice(0, -1);
  };

  const onSwitchAuth = async () => {
    if (!staff) return;

    const res = await dispatch(doSwitchAuth(staff.memberCode));

    switch (res?.status) {
      case 200:
        history.push('/');
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(
          res?.data?.data || t('common.message.error'),
          errorVariant,
        );
    }
  };

  const onSetHousingInput = (key: string, value: any) => {
    setHousingInput({ ...housingInput, [key]: value });
  };

  const renderTabContent = () => {
    switch (content) {
      // 기본정보
      case EmployeeContents.Info:
        return <StaffInfo staff={staff} getStaff={getStaff} />;
      // 이력서관리
      case EmployeeContents.Resume:
        return <StaffResume staff={staff} memberCode={memberCode} />;
      // 근로계약서
      case EmployeeContents.Contract:
        return <StaffContract staff={staff} memberCode={memberCode} />;
      // 상담관리
      case EmployeeContents.Counsel:
        return <StaffCounsel memberCode={memberCode} />;
      // 하우징관리
      case EmployeeContents.Housing:
        return (
          <StaffHousing housing={housing} onOpenEditor={onOpenHousingEditor} />
        );
      // 하우징관리 등록/수정
      case EmployeeContents.HousingEdit:
        return (
          <StaffHousingEditor
            staff={staff}
            housing={housing}
            housingInput={housingInput}
            housingInputInit={housingInputInit}
            setHousingInput={setHousingInput}
            onSetHousingInput={onSetHousingInput}
            setTab={onSetTab}
          />
        );
    }
  };

  const onOpenHousingEditor = () => {
    if (!staff) {
      return enqueueSnackbar(t('campus.staff.message7'), {
        variant: 'info',
      });
    }
    if (housing) {
      const input: any = _.cloneDeep(housing);
      setHousingInput(input);
    }

    onSetTab(EmployeeContents.HousingEdit);
  };

  return (
    <>
    <StaffSearch
      groupNo={groupNo}
      setGroupNo={setGroupNo}
      keyword={keyword}
      setKeyword={setKeyword}
      onSearch={onSearch}
      employeeStatus={employeeStaus}
      setEmployeeStatus={setEmployeeStatus}
    />

    <Block className="content">
      {isLoading || staffIsLoading || housingIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <SimpleHeader
        iconFile="icon-campus-big.png"
        parentMenu={t('common.menu.campus')}
        currentMenu={t('campus.staff.title1')}
        onlyTitle={true}
      />

      <Split 
        className='splite-divide'
        sizes={[37, 63]}
        minSize={100}
        expandToMin={false}
        gutterSize={10}
        gutterAlign="center"
        snapOffset={30}
        dragInterval={1}
        direction="horizontal"
        cursor="col-resize"
      >
        <div className='col-frame left_panel'>
          <StaffTable
            employees={res?.data || []}
            groupNo={query.groupNo ? String(query.groupNo) : undefined}
            keyword={query.keyword ? String(query.keyword) : undefined}
            page={query.page ? Number(query.page) : 1}
            param={param}
            onSearch={onSearch}
            memberCode={memberCode}
            employeeStatus={
              query.employeeStaus ? String(query.employeeStaus) : undefined
            }
          />
        </div>
        <div className='col-frame right_panel'>
          {
            // 본사 직원 & 원장만 직원등록 가능
            auth && (auth.hqYn === 'Y' || auth.authGroupDiv === 'O') ? (
              <div className="comm-bx__between">
                <div />
                <div className="comm-btn__area right">
                  <Button
                    className="btn-basic pink"
                    onClick={() =>
                      history.push(
                        `/campus/staff/create?${qs.stringify(param)}`,
                      )
                    }
                    text={t('campus.staff.button5')}
                    writeAuthYn={writeAuthYn}
                    disabled={campusDeactived}
                  />
                </div>
              </div>
            ) : null
          }
          <StaffSummary
            staff={staff}
            onSwitchAuth={onSwitchAuth}
            renderCampusName={renderCampusName}
            onEditStaff={onEditStaff}
            setAddCampusVisible={setAddCampusVisible}
            campusDeactived={campusDeactived}
          />

          <div>
            <div className="w-100">
              <div className="comm-tab__line">
                <ul>
                  <li className={(content === EmployeeContents.Info ? ' active' : '')}>
                    <a
                      className="btn-item"
                      onClick={() => onSetTab(EmployeeContents.Info)}
                    >
                      {t('campus.staff.tab1')}
                    </a>
                  </li>
                  <li className={(content === EmployeeContents.Resume ? ' active' : '')}>
                    <a
                      className="btn-item"
                      onClick={() => onSetTab(EmployeeContents.Resume)}
                    >
                      {t('campus.staff.tab2')}
                    </a>
                  </li>
                  <li className={(content === EmployeeContents.Contract ? ' active' : '')}>
                    <a
                      className="btn-item"
                      onClick={() => onSetTab(EmployeeContents.Contract)}
                    >
                      {t('campus.staff.tab3')}
                    </a>
                  </li>
                  <li className={(content === EmployeeContents.Counsel ? ' active': '')}>
                    <a
                      className="btn-item"
                      onClick={() => onSetTab(EmployeeContents.Counsel)}
                    >
                      {t('campus.staff.tab4')}
                    </a>
                  </li>
                  <li className={(content === EmployeeContents.Housing ||
                                  content === EmployeeContents.HousingEdit
                                  ? ' active'
                                  : '')}>
                    <a
                      className="btn-item"
                      onClick={() => onSetTab(EmployeeContents.Housing)}
                    >
                      {t('campus.staff.tab5')}
                    </a>
                  </li>
                </ul>
              </div>
              <div className="tab-content" id="custom-tabs-tabContent">
                {staff ? (
                  <div>{renderTabContent()}</div>
                ) : (
                  <TableEmpty text={t('campus.staff.text4')} />
                )}
              </div>
            </div>
          </div>
        </div>
      </Split>

      {addCampusVisible && (
        <AddCampusModal
          auth={auth}
          campusData={campusData}
          addCampusVisible={addCampusVisible}
          setAddCampusVisible={setAddCampusVisible}
          addCampus={addCampus}
          campusToAdd={campusToAdd}
          setCampusToAdd={setCampusToAdd}
        />
      )}
    </Block>
    </>
  );
};

export default CampusStaff;
